import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { adminLogin, adminForgotPassword } from "@/lib/api";
import { adminInputCls, adminGoldBtnCls } from "./shared";

export const LoginForm = ({ onLogin }: { onLogin: (token: string) => void }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  const doLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await adminLogin(email, password, remember);
      localStorage.setItem("r3-admin-token", r.data.token);
      localStorage.setItem("r3-admin-email", r.data.email || email);
      onLogin(r.data.token);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    }
    setBusy(false);
  };

  const doForgot = async () => {
    if (!email.trim()) {
      toast.error("Enter your admin email first");
      return;
    }
    try {
      await adminForgotPassword(email.trim());
      toast.success("If that email belongs to an admin account, a password reset link is on its way to your inbox.");
    } catch {
      toast.error("Could not process the request");
    }
  };

  return (
    <main data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-ink px-5">
      <form
        data-testid="admin-login-form"
        onSubmit={doLogin}
        className="w-full max-w-sm border border-white/10 bg-[#0d0d0d] p-8"
      >
        <p className="font-display text-2xl font-black uppercase tracking-tight text-white">
          R3Stan <span className="text-gold">Admin</span>
        </p>
        <p className="mt-2 text-xs uppercase tracking-[0.2em] text-neutral-500">
          Private dashboard
        </p>
        <input
          data-testid="admin-email-input"
          type="email"
          required
          placeholder="Admin email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={`${adminInputCls} mt-8`}
        />
        <input
          data-testid="admin-password-input"
          type="password"
          required
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={`${adminInputCls} mt-4`}
        />
        <label
          data-testid="admin-remember-label"
          className="mt-4 flex cursor-pointer items-center gap-2 text-xs text-neutral-400"
        >
          <input
            data-testid="admin-remember-checkbox"
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="h-4 w-4"
          />
          Remember me for 30 days
        </label>
        <button
          data-testid="admin-login-btn"
          type="submit"
          disabled={busy}
          className={`${adminGoldBtnCls} mt-6 w-full`}
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Sign In
        </button>
        <button
          data-testid="admin-forgot-btn"
          type="button"
          onClick={doForgot}
          className="mt-4 w-full text-center text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500 transition-colors duration-300 hover:text-gold"
        >
          Forgot password?
        </button>
      </form>
    </main>
  );
};
